import { Shape } from '@/services/shape';
import { ImageArrayAttribute } from '@/services/shape/attributes/image-array';
import {
  addResourceIfNotExists,
  generateLayoutAst,
  getImageArrayWidgetMaxSize,
  insertImages
} from '../../utils/resource.utils';
import { DataSourceFormat } from '@/resources/enum';
import { generateResourceName, getRefName } from '../../utils/name.utils';

/**
 * 当使用月日数据源，用widget包含多个数据源元素，拼接生成ast
 ```
// 支持以下数据源，不涉及多单位
 'date',
 ```
 * @param resourceAst
 * @param shape
 * @returns
 */
export const getImageArrayWidgetDate = (
  resourceAst: Ast,
  shape: Shape,
  parent: StoreLayerItem | undefined,
  /** 全局缩放×元素缩放，用于子元素 _scale 与 Widget 尺寸，不传则用 shape.attrs.scale */ effectiveScale?: number
) => {
  const {
    x = 0,
    y = 0,
    resources,
    sourceFormat = 1,
    space = 0,
    widgetSpace = 0,
    recolorEnable = false,
    scale = 1,
    alpha = 1
  } = shape.attrs as ImageArrayAttribute;
  const scaleForChildren = effectiveScale ?? scale;

  // 添加图片
  const imageMap = insertImages(resourceAst, resources, {
    recolorEnable,
    scale: scaleForChildren,
    alpha
  });
  const _dataItemImageNumber = (options: {
    leadingZero: boolean;
    source: string;
  }) => {
    const { source, leadingZero } = options;
    const resourceItem: Ast = {
      tag: 'DataItemImageNumber',
      attrsMap: {
        source: source,
        ref: getRefName(imageMap['images']),
        rotation: '0',
        leadingZero: leadingZero ? 'true' : 'false',
        decimalOffsetX: '0',
        trailingZero: 'false',
        totalDigits: '2',
        decimalDigits: '0',
        supportRecolor: String(recolorEnable),
        parameter: '1000',
        renderRule: 'alwaysShow',
        align: 'left',
        space: String(space)
      },
      children: []
    };

    const ast = addResourceIfNotExists(resourceAst, resourceItem);

    // 生成 layout ast
    const layoutAst: Ast = generateLayoutAst(
      {
        ref: '@' + ast.attrsMap.name,
        x,
        y
      },
      'Item'
    );
    return layoutAst;
  };

  const _imageUnitIcon = (resourceName: 'unitIcon' | 'unitIcon2') => {
    const layoutAst: Ast = generateLayoutAst(
      {
        ref: getRefName(imageMap[resourceName]),
        x,
        y
      },
      'Item'
    );
    return layoutAst;
  };

  const child1 = _dataItemImageNumber({
    leadingZero: [
      DataSourceFormat['Format_08/16'],
      DataSourceFormat['Format_08月16日']
    ].includes(sourceFormat),
    source: 'dateMonth'
  });
  const child2 = _imageUnitIcon('unitIcon');
  const child3 = _dataItemImageNumber({
    leadingZero: [
      DataSourceFormat['Format_8/16'],
      DataSourceFormat['Format_8月16日']
    ].includes(sourceFormat)
      ? false
      : true,
    source: 'dateDay'
  });
  const children = [child1, child2, child3];

  // 如果需要第二个单位
  const _unitIcon2 = [
    DataSourceFormat['Format_8月16日'],
    DataSourceFormat['Format_08月16日']
  ].includes(sourceFormat);
  if (_unitIcon2) {
    children.push(_imageUnitIcon('unitIcon2'));
  }
  children.forEach((item, index) => {
    item.attrsMap._id = shape.id + '_' + index;
    // 添加缩放值/透明度（使用 effectiveScale 使图组日期在全局缩放下生效）
    item.attrsMap._scale = String(scaleForChildren);
    item.attrsMap._alpha = String(alpha);
  });

  const { width, height } = getImageArrayWidgetMaxSize({
    resources,
    gap: widgetSpace,
    space: space,
    format: _unitIcon2 ? '08月16日' : '08/16'
  });
  const widgetAst: Ast = {
    tag: 'Widget',
    attrsMap: {
      name: generateResourceName(resourceAst, 'Widget'),
      flex_direction: 'row',
      justify_content: 'flex-start',
      align_items: 'center',
      gap: String(widgetSpace),
      w: String(width * scaleForChildren),
      h: String(height * scaleForChildren),
      _asWidget: 'image-array-date'
    },
    children: children
  };
  if (parent?.attrs.minClickZone) widgetAst.attrsMap.minClickZone = 'true';

  const _ast = addResourceIfNotExists(resourceAst, widgetAst);
  const layoutAst: Ast = generateLayoutAst({
    ref: '@' + _ast.attrsMap.name,
    x,
    y
  });

  return layoutAst;
};
